import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, Search, FileText, Link2, Zap, Globe, Settings, MapPin,
  ChevronDown, ChevronUp, Mail, Phone, CheckCircle2, TrendingUp, 
  Target, BarChart3, Send
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import SEOHead from '../components/SEOHead';
import { getPaths } from '../utils/paths';

const FALCO_PHOTO = "https://customer-assets.emergentagent.com/job_54396c88-8cf9-4315-926b-d20a7ab432d7/artifacts/uqz5vdch_2026-03-21_13-47-36.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const { language, t } = useLanguage();
  const basePath = language === 'en' ? '/en' : '';
  const paths = getPaths(language);
  const [openFaq, setOpenFaq] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', website: '', message: '' });
  const [sending, setSending] = useState(false);

  const services = [
    {
      icon: <Search className="h-6 w-6" />,
      title: language === 'nl' ? 'SEO Strategie' : 'SEO Strategy',
      badge: 'Populair',
      description: language === 'nl' 
        ? 'Effectieve SEO-strategieën die daadwerkelijk werken. Data-gedreven en resultaatgericht.' 
        : 'Gain organic visibility in the Dutch market. Tailored strategies for Google.nl.',
      href: paths.service('seo-strategie')
    },
    {
      icon: <FileText className="h-6 w-6" />,
      title: language === 'nl' ? 'SEO Teksten' : 'Dutch SEO Content',
      description: language === 'nl' 
        ? 'SEO-teksten die aansluiten bij de zoekintentie van jouw doelgroep en converteren.'
        : 'Native Dutch content that matches local search intent and converts Dutch audiences.',
      href: paths.service('seo-tekstschrijver')
    },
    {
      icon: <Settings className="h-6 w-6" />,
      title: language === 'nl' ? 'Technische SEO' : 'Technical Localisation',
      description: language === 'nl' 
        ? 'Diepgaande audits die elke stap concreet en uitvoerbaar maken.'
        : 'Hreflang, .nl domain setup, and technical audits for the Dutch market.',
      href: paths.service('technische-seo')
    },
    {
      icon: <Link2 className="h-6 w-6" />,
      title: language === 'nl' ? 'Linkbuilding' : 'Dutch Link Building',
      description: language === 'nl' 
        ? 'Een realistische linkbuilding strategie op basis van concurrentieanalyse, met een helder budget om te ranken op jouw producten en diensten.'
        : 'Build authority with backlinks from Dutch domains. Strategy based on competitor analysis in your Dutch target market.',
      href: paths.service('linkbuilding')
    },
    {
      icon: <Globe className="h-6 w-6" />,
      title: language === 'nl' ? 'SEO Consultancy' : 'Dutch Market Consultancy',
      description: language === 'nl' 
        ? 'Heb je al een SEO-strategie en wil je een second opinion? Ik kijk graag mee.'
        : 'Already active in the Netherlands? Get a strategic review of your Dutch SEO performance.',
      href: paths.service('seo-consultancy')
    },
    {
      icon: <Globe className="h-6 w-6" />,
      title: 'GEO',
      description: language === 'nl' 
        ? 'Vindbaar worden in AI-driven zoekresultaten. De volgende stap in zoekmachineoptimalisatie.'
        : 'Get your brand cited in AI-driven search results in the Dutch market.',
      href: paths.service('geo')
    }
  ];

  const faqItems = [
    {
      q: language === 'nl' ? 'Hoe werkt de samenwerking?' : 'How does working with a Dutch SEO partner work?',
      a: language === 'nl' 
        ? 'Ik werk in sprints van 3 maanden. Elk kwartaal bepalen we samen wat er moet gebeuren de komende periode en het aantal uren dat daarvoor nodig is. Het eerste kwartaal heb ik altijd de meeste uren nodig omdat ik dan de strategie uitzet. Daarna moet het uitgevoerd worden en komen we uiteindelijk in de onderhoudsfase.'
        : 'I work in 3-month sprints tailored to your Dutch market entry. Each quarter we set priorities together. The first sprint focuses heavily on strategy — Dutch keyword research, competitor analysis and localisation planning. After that, execution begins and we eventually move into the maintenance phase.'
    },
    {
      q: language === 'nl' ? 'Waarom Kesult?' : 'Why choose a local Dutch SEO specialist?',
      a: language === 'nl' 
        ? 'Ik werk hoofdzakelijk strategisch. Dat betekent dat ik strategieën uitwerk en samenwerk met in-house marketeers, developers of specialisten die het uitvoerende werk doen, of partners met wie ik samenwerk zoals content schrijvers of linkbuilding partners. Zo wordt iedereen in zijn kracht gebruikt. Voor elk kwartaal adviseer ik het aantal uren op basis van resources, prioriteiten en financiële middelen — samen bepalen we wat het beste past.'
        : 'The Dutch market has unique search behaviour and language nuances that generic international agencies often miss. I bring local expertise — native Dutch keyword research, connections with Dutch publishers for link building, and an understanding of how Google.nl ranks differently. I work strategically and collaborate with your in-house team or local partners for execution.'
    },
    {
      q: language === 'nl' ? 'Zijn contracten maandelijks opzegbaar?' : 'Are contracts cancellable monthly?',
      a: language === 'nl' 
        ? 'Ik werk in sprints van 3 maanden. Deze zijn per kwartaal opzegbaar tot het einde van de tweede maand van een kwartaal. Als je besluit om de samenwerking op te zeggen, dan moet dat voor de start van de derde maand.'
        : 'I work in 3-month sprints. These are cancellable per quarter until the end of the second month. If you decide to end the collaboration, it must be before the start of the third month.'
    },
    {
      q: language === 'nl' ? 'Wat is het uurtarief?' : 'What is the hourly rate?',
      a: language === 'nl' 
        ? 'Het standaard uurtarief is \u20ac95 per uur. Dit bedrag betaal je alleen bij een eenmalig consult (1 uur). Bij een SEO pakket geldt er een korting die afhankelijk is van het pakket, variërend van 5% tot 15%.'
        : 'The standard hourly rate is \u20ac95 per hour. This amount only applies for a one-time consultation (1 hour). With an SEO package, a discount applies depending on the package, ranging from 5% to 15%.'
    },
    {
      q: language === 'nl' ? 'Bied je ook eenmalige diensten aan?' : 'Do you offer one-off services?',
      a: language === 'nl' 
        ? 'Naast strategische trajecten bied ik gerichte, eenmalige diensten aan voor specifieke vraagstukken. Je ontvangt na afloop een diepgaande analyse met concrete actiepunten waarmee jouw team direct aan de slag kan.'
        : 'In addition to strategic projects, I offer targeted one-off services for specific issues. You receive an in-depth analysis with concrete action items that your team can start working on immediately.'
    },
    {
      q: language === 'nl' ? 'Geef je garantie op SEO-succes?' : 'Do you guarantee SEO success?',
      a: language === 'nl' 
        ? 'Ik sta volledig achter mijn expertise en de resultaten die ik voor mijn klanten behaal. Toch is het belangrijk om transparant te zijn: in de wereld van zoekmachines bestaan geen garanties. Google is een externe factor met algoritmes die continu veranderen. Wat ik wel garandeer, is een aanpak die gebaseerd is op jarenlange ervaring en bewezen strategieën. SEO is een proces van lange adem en constante bijsturing. Mocht een algoritme-update invloed hebben op de posities, dan gebruik ik mijn kennis om de strategie direct te analyseren en waar nodig te corrigeren. Mijn focus ligt op duurzame groei en autoriteit, in plaats van vluchtige resultaten.'
        : 'I fully stand behind my expertise and the results I achieve for my clients. However, it is important to be transparent: in the world of search engines there are no guarantees. Google is an external factor with algorithms that constantly change. What I do guarantee is an approach based on years of experience and proven strategies. My focus is on sustainable growth and authority, rather than fleeting results.'
    },
    {
      q: language === 'nl' ? 'Hoe snel zie ik resultaat van de SEO strategie?' : 'How quickly will I see results?',
      a: language === 'nl' 
        ? 'SEO is een investering in duurzame groei. Hoewel technische fixes soms direct effect hebben, duurt het gemiddeld 3 tot 6 maanden voordat de effecten van een nieuwe content- of autoriteitsstrategie volledig zichtbaar zijn in de zoekresultaten. Daarom werk ik in sprints van 3 maanden, zodat we voldoende data opbouwen om de impact meetbaar te maken.'
        : 'SEO is an investment in sustainable growth. While technical fixes sometimes have immediate effect, it takes on average 3 to 6 months before the effects of a new content or authority strategy are fully visible in search results. That is why I work in 3-month sprints, so we build sufficient data to make the impact measurable.'
    },
    {
      q: language === 'nl' ? 'Hoe blijf ik op de hoogte van de werkzaamheden?' : 'How do I stay informed about the work?',
      a: language === 'nl' 
        ? 'Transparantie staat centraal. Via het centrale werkdocument heb je op elk moment inzicht in de actiepunten, de prioriteiten en de status van de uitvoering. Daarnaast plannen we periodieke overlegmomenten om de voortgang te bespreken en de strategie bij te sturen op basis van de meest recente data.'
        : 'Transparency is key. Via the central work document you have insight at any time into the action items, priorities and status of execution. In addition, we schedule periodic meetings to discuss progress and adjust the strategy based on the most recent data.'
    },
    {
      q: language === 'nl' ? 'Wat gebeurt er na de eerste sprint van 3 maanden?' : 'What happens after the first 3-month sprint?',
      a: language === 'nl' 
        ? 'Na elke sprint evalueren we de resultaten en de voortgang. Op basis daarvan bepalen we of we een nieuwe sprint starten om verder op te schalen, of dat we overgaan naar de onderhoudsfase om de behaalde posities te bewaken. Je zit dus nooit vast aan langdurige, ondoorzichtige contracten.'
        : 'After each sprint we evaluate the results and progress. Based on that, we determine whether to start a new sprint to scale further, or transition to the maintenance phase to protect achieved positions. You are never locked into long-term, opaque contracts.'
    },
    {
      q: language === 'nl' ? 'Waar ben je gevestigd?' : 'Where are you based and do you work internationally?',
      a: language === 'nl' 
        ? 'Ik ben gevestigd in Veenendaal, Nederland. Ik werk voornamelijk op afstand, wat betekent dat ik geen dure overhead heb die doorberekend wordt in mijn tarieven. Meetings doe ik graag via video, maar een persoonlijke afspraak is altijd mogelijk.'
        : 'I am based in Veenendaal, The Netherlands. I work primarily remotely, which makes collaborating across time zones seamless. I regularly work with international businesses expanding to the Dutch market. Meetings are usually via video call, but in-person meetings in the Netherlands are always possible.'
    }
  ];

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        ...contactForm,
        lead_type: 'contact',
        language: language
      });
      toast.success(language === 'nl' ? 'Bericht verzonden!' : 'Message sent!');
      setContactForm({ name: '', email: '', phone: '', website: '', message: '' });
    } catch (error) {
      toast.error(language === 'nl' ? 'Er ging iets mis.' : 'Something went wrong.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="home-page">
      <SEOHead
        title={language === 'nl' ? 'Freelance SEO Specialist | Meer organische groei voor jouw business' : 'Freelance SEO Specialist Netherlands | Boost organic growth in the Dutch market'}
        description={language === 'nl'
          ? 'Zoek je een freelance SEO specialist? Falco Keiman helpt ambitieuze bedrijven met holistische SEO-strategieën. Boek een vrijblijvende kennismaking.'
          : 'Need a native Dutch SEO specialist? I help international businesses gain organic visibility in the Netherlands. Book a free introduction call.'}
        canonical="https://www.kesult.com/"
        lang={language}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "Kesult",
            "url": "https://www.kesult.com",
            "description": "Freelance SEO Specialist Nederland",
            "founder": { "@type": "Person", "name": "Falco Keiman", "jobTitle": "Freelance SEO Specialist" },
            "address": { "@type": "PostalAddress", "addressLocality": "Veenendaal", "addressCountry": "NL" },
            "areaServed": "NL",
            "priceRange": "€€"
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Kesult",
            "url": "https://www.kesult.com"
          }
        ]}
      />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-white overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="max-w-xl pt-8 lg:pt-0">
              <h1 
                className="text-[2.75rem] sm:text-[3.5rem] lg:text-[4rem] font-bold leading-[1.1] tracking-tight text-[#08080A] mb-6"
                data-testid="hero-title"
              >
                {language === 'nl' ? (
                  <>Freelance{' '}<span className="text-[#4286F4]">SEO</span>{' '}Consultant</>
                ) : (
                  <>Your Freelance{' '}<span className="text-[#4286F4]">SEO</span>{' '}Specialist in the Netherlands</>
                )}
              </h1>
              
              <p className="text-lg text-[#555] leading-relaxed mb-8">
                {language === 'nl' 
                  ? <>Hey! Ik ben <strong className="text-[#08080A]">Falco</strong>. Ik bied freelance SEO consultancy voor ambitieuze ondernemers en groeiende MKB-bedrijven in Nederland. Van startups tot scale-ups — ik heb een no-nonsense aanpak en lever kwaliteit die resultaat oplevert.</>
                  : <>Hey! I'm <strong className="text-[#08080A]">Falco</strong>. I help international businesses to boost organic growth in the Netherlands through strategic SEO. From Dutch keyword research to local link building — I bridge the gap between your brand and the Dutch consumer.</>
                }
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to={`${basePath}/contact`}>
                  <button 
                    className="bg-[#4286F4] text-white font-semibold px-8 py-3.5 rounded-full text-base hover:bg-[#3070d4] transition-colors duration-200 inline-flex items-center gap-2"
                    data-testid="hero-cta-primary"
                  >
                    {language === 'nl' ? 'Neem contact op' : 'Get in touch'}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </Link>
                <a href="mailto:info@kesult.com">
                  <button 
                    className="border-2 border-[#E5E5E5] text-[#333] font-semibold px-8 py-3.5 rounded-full text-base hover:border-[#4286F4] hover:text-[#4286F4] transition-colors duration-200 inline-flex items-center gap-2"
                    data-testid="hero-cta-secondary"
                  >
                    <Mail className="h-4 w-4" />
                    {language === 'nl' ? 'Mail me' : 'Email me'}
                  </button>
                </a>
              </div>
            </div>

            {/* Photo */}
            <div className="hidden lg:flex justify-end">
              <img 
                src={FALCO_PHOTO}
                alt="Falco Keiman - Freelance SEO Specialist"
                className="w-full max-w-[440px] aspect-[3/4] object-cover object-top rounded-3xl"
                data-testid="hero-photo"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 lg:py-28 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-4">
            <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
              {language === 'nl' ? 'Wat ik doe' : 'Dutch Market SEO'}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#08080A] mb-4">
              {language === 'nl' ? 'Freelance SEO Diensten' : 'SEO Services for the Dutch Market'}
            </h2>
            <p className="text-lg text-[#555] leading-relaxed">
              {language === 'nl' 
                ? 'Als freelance SEO specialist is mijn doel om je te voorzien van een volledig op maat gemaakte, data-gedreven strategie die jouw organische zichtbaarheid daadwerkelijk laat groeien.'
                : 'Everything you need to gain organic visibility in the Netherlands. From technical localisation to native Dutch content — I provide the local expertise your international brand needs.'}
            </p>
          </div>

          <div className="mb-8">
            <Link to={paths.diensten} className="inline-flex items-center gap-2 text-[#4286F4] font-semibold text-sm hover:underline">
              {language === 'nl' ? 'Bekijk alle diensten' : 'View all services'}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Link 
                key={index}
                to={service.href}
                className="group p-8 rounded-2xl border border-gray-100 bg-white hover:shadow-lg hover:border-[#4286F4]/20 transition-all duration-300"
                data-testid={`service-card-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-bold text-[#08080A] group-hover:text-[#4286F4] transition-colors">
                    {service.title}
                  </h3>
                  {service.badge && (
                    <span className="bg-[#4286F4] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
                      {service.badge}
                    </span>
                  )}
                </div>
                <p className="text-[#666] text-sm leading-relaxed mb-6">
                  {service.description}
                </p>
                <span className="inline-flex items-center gap-2 text-[#4286F4] font-semibold text-sm group-hover:gap-3 transition-all">
                  {language === 'nl' ? 'Meer info' : 'Find out more'}
                  <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>

          <div className="mt-8">
            <Link to={paths.diensten} className="inline-flex items-center gap-2 text-[#4286F4] font-semibold text-sm hover:underline">
              {language === 'nl' ? 'Bekijk alle diensten' : 'View all services'}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Werkwijze - 5-Stappen Proces */}
      <section className="py-20 lg:py-28 bg-[#FAFAFA]" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
            {language === 'nl' ? 'Mijn werkwijze' : 'How I Work'}
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] max-w-3xl mb-4 leading-snug">
            {language === 'nl' 
              ? 'Het 5-stappen proces naar organische groei'
              : 'The 5-step process to Dutch market visibility'}
          </h2>
          <p className="text-base text-[#555] leading-relaxed max-w-2xl mb-14">
            {language === 'nl' 
              ? 'Gestoeld op data, transparantie en strakke regie.'
              : 'A structured approach to your Dutch market entry — built on data and transparency.'}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
            {[
              { step: '01', title: language === 'nl' ? 'Vrijblijvende Kennismaking' : 'Free Introduction', sub: language === 'nl' ? 'Een digitale kennismaking om jouw bedrijf, ambities en resources te bespreken.' : 'A digital introduction to discuss your business, ambitions and resources.' },
              { step: '02', title: language === 'nl' ? 'Voorstel op Maat' : 'Bespoke Proposal', sub: language === 'nl' ? 'Eerlijk advies op basis van analyse, budget en ambitie.' : 'Honest advice based on analysis, budget and ambition.' },
              { step: '03', title: language === 'nl' ? 'Strategisch Plan' : 'Strategic Plan', sub: language === 'nl' ? 'Een audit op basis van de 3 SEO-pijlers en de huidige status van je website. Daaruit volgt een strategisch plan gefocust op Technical Excellence, Content en Autoriteit.' : 'An audit based on the 3 SEO pillars and your website\'s current status, resulting in a strategic plan focused on Technical Excellence, Content and Authority.' },
              { step: '04', title: language === 'nl' ? 'SEO Executiefase' : 'SEO Execution Phase', sub: language === 'nl' ? 'Hier wordt de strategie uitgevoerd die uitgewerkt is in het strategisch plan.' : 'This is where the strategy developed in the strategic plan is executed.' },
              { step: '05', title: language === 'nl' ? 'Onderhoudsfase' : 'Maintenance Phase', sub: language === 'nl' ? 'In deze fase wordt alles onderhouden en bijgestuurd.' : 'In this phase, everything is maintained and adjusted.' },
            ].map((item) => (
              <div key={item.step} className="group">
                <span className="text-3xl font-bold text-[#4286F4] block mb-3">{item.step}</span>
                <div className="h-px w-full bg-gray-300 group-hover:bg-[#4286F4] transition-colors mb-4" />
                <h3 className="text-base font-bold text-[#08080A] mb-2">{item.title}</h3>
                <p className="text-sm text-[#555] leading-relaxed">{item.sub}</p>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <Link to={paths.werkwijze} className="inline-flex items-center gap-2 text-[#4286F4] font-semibold text-sm hover:underline">
              {language === 'nl' ? 'Lees meer over mijn werkwijze' : 'Read more about my process'}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-[#4286F4] text-white" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <h2 className="text-2xl sm:text-3xl font-bold">
            {language === 'nl' ? 'Klaar voor een SEO-strategie op maat?' : 'Ready for a bespoke SEO strategy?'}
          </h2>
          <div className="flex gap-4">
            <Link to={`${basePath}/contact`}>
              <button className="bg-white text-[#4286F4] font-semibold px-8 py-3.5 rounded-full hover:bg-gray-50 transition-colors inline-flex items-center gap-2" data-testid="cta-button">
                {language === 'nl' ? 'Neem contact op' : 'Get in touch'}
                <ArrowRight className="h-4 w-4" />
              </button>
            </Link>
            <a href="mailto:info@kesult.com">
              <button className="border-2 border-white/30 text-white font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors">
                {language === 'nl' ? 'Mail me' : 'Email me'}
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ / About Section */}
      <section className="py-20 lg:py-28 bg-[#FAFAFA]" data-testid="faq-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Left Column - Intro + first half of FAQs */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-4">
                {language === 'nl' ? 'Over Mijn SEO Diensten' : 'About My SEO Services'}
              </h2>
              <p className="text-base text-[#555] leading-relaxed mb-12">
                {language === 'nl' 
                  ? 'Of je nu een startup hebt die marketingdiensten nodig heeft, of je werkt bij een groeiend bedrijf dat op zoek is naar SEO consultancy — ik kan helpen.'
                  : 'Whether you own a startup requiring marketing services, or work in marketing for a growing company looking for SEO consultancy, I can help.'}
              </p>

              <div>
                {faqItems.slice(0, Math.ceil(faqItems.length / 2)).map((item, index) => (
                  <div key={index} className="border-b border-gray-300" data-testid={`faq-item-${index}`}>
                    <button 
                      className="w-full flex items-center justify-between py-5 text-left group"
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    >
                      <span className="text-[15px] font-medium text-[#08080A] group-hover:text-[#4286F4] transition-colors pr-4">
                        {item.q}
                      </span>
                      <span className="flex-shrink-0 text-xl font-light text-[#08080A]">
                        {openFaq === index ? '\u2212' : '+'}
                      </span>
                    </button>
                    {openFaq === index && (
                      <div className="pb-5 pr-8">
                        <p className="text-sm text-[#555] leading-relaxed">{item.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column - second half of FAQs */}
            <div className="lg:pt-[120px]">
              {faqItems.slice(Math.ceil(faqItems.length / 2)).map((item, index) => {
                const realIndex = index + Math.ceil(faqItems.length / 2);
                return (
                  <div key={realIndex} className="border-b border-gray-300" data-testid={`faq-item-${realIndex}`}>
                    <button 
                      className="w-full flex items-center justify-between py-5 text-left group"
                      onClick={() => setOpenFaq(openFaq === realIndex ? null : realIndex)}
                    >
                      <span className="text-[15px] font-medium text-[#08080A] group-hover:text-[#4286F4] transition-colors pr-4">
                        {item.q}
                      </span>
                      <span className="flex-shrink-0 text-xl font-light text-[#08080A]">
                        {openFaq === realIndex ? '\u2212' : '+'}
                      </span>
                    </button>
                    {openFaq === realIndex && (
                      <div className="pb-5 pr-8">
                        <p className="text-sm text-[#555] leading-relaxed">{item.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Bespoke Services Section */}
      <section className="py-20 lg:py-28 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
                {language === 'nl' ? 'Mijn aanpak' : 'Why a Local Partner'}
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-6">
                {language === 'nl' ? 'Op maat gemaakte, kosteneffectieve SEO' : 'Your Dutch market entry deserves local expertise'}
              </h2>
              <p className="text-[#555] leading-relaxed mb-6">
                {language === 'nl' 
                  ? 'Ik werk hoofdzakelijk strategisch. Dat betekent dat ik strategieën uitwerk en samenwerk met in-house marketeers, developers of specialisten die het uitvoerende werk doen, of partners met wie ik samenwerk zoals content schrijvers of linkbuilding partners. Zo wordt iedereen in zijn kracht gebruikt.'
                  : 'The Dutch market has its own search behaviour, language nuances and competitive landscape. A direct translation of your existing content rarely works. You need someone who understands how Dutch consumers search, what they expect and how Google.nl ranks content differently.'}
              </p>
              <p className="text-[#555] leading-relaxed">
                {language === 'nl' 
                  ? 'Voor elk kwartaal adviseer ik het aantal uren op basis van resources, prioriteiten en financiële middelen — samen bepalen we wat het beste past. Het eerste kwartaal heb ik altijd de meeste uren nodig omdat ik dan de strategie uitzet. Daarna moet het uitgevoerd worden en komen we uiteindelijk in de onderhoudsfase.'
                  : 'I work as your strategic SEO partner on the ground: handling Dutch keyword research, content localisation strategy, local link building and technical SEO — while collaborating closely with your in-house team or agency abroad.'}
              </p>
            </div>
            <div>
              <img 
                src={FALCO_PHOTO}
                alt="Falco Keiman"
                className="w-full max-w-md mx-auto aspect-square object-cover object-top rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 lg:py-28 bg-white" id="contact" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#08080A] mb-8">
                Let's Talk About<br /><span className="text-[#4286F4]">{language === 'nl' ? 'Organic Growth' : 'Organic Growth in the Netherlands'}</span>
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#F1F0EF] rounded-xl flex items-center justify-center">
                    <Mail className="h-5 w-5 text-[#4286F4]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#999] uppercase tracking-wider font-semibold">Email</p>
                    <a href="mailto:info@kesult.com" className="text-[#08080A] font-semibold hover:text-[#4286F4] transition-colors">
                      info@kesult.com
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#F1F0EF] rounded-xl flex items-center justify-center">
                    <Phone className="h-5 w-5 text-[#4286F4]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#999] uppercase tracking-wider font-semibold">{language === 'nl' ? 'Telefoon' : 'Phone'}</p>
                    <a href="tel:+31612614620" className="text-[#08080A] font-semibold hover:text-[#4286F4] transition-colors">
                      +31 6 1261 4620
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#F1F0EF] rounded-xl flex items-center justify-center">
                    <svg className="h-5 w-5 text-[#4286F4]" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  </div>
                  <div>
                    <p className="text-xs text-[#999] uppercase tracking-wider font-semibold">LinkedIn</p>
                    <a href="https://www.linkedin.com/in/falco-keiman/" target="_blank" rel="noopener noreferrer" className="text-[#08080A] font-semibold hover:text-[#4286F4] transition-colors">
                      Falco Keiman
                    </a>
                  </div>
                </div>

                <p className="text-sm text-[#999] mt-4 pt-4 border-t border-gray-100">
                  {language === 'nl' 
                    ? 'Ik werk volledig remote.' 
                    : 'I work fully remote.'}
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <p className="text-sm text-[#999] mb-6 uppercase tracking-wider font-semibold">
                {language === 'nl' ? 'Stuur een bericht' : 'Send a message'}
              </p>
              <form onSubmit={handleContactSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-[#333] mb-1.5">
                      {language === 'nl' ? 'Naam' : 'Name'}
                    </label>
                    <input 
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      className="w-full h-12 px-4 border border-gray-200 rounded-xl text-sm focus:border-[#4286F4] focus:outline-none transition-colors"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#333] mb-1.5">
                      {language === 'nl' ? 'Telefoon' : 'Phone'}
                    </label>
                    <input 
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                      className="w-full h-12 px-4 border border-gray-200 rounded-xl text-sm focus:border-[#4286F4] focus:outline-none transition-colors"
                      data-testid="contact-phone"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-[#333] mb-1.5">
                      Email
                    </label>
                    <input 
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      className="w-full h-12 px-4 border border-gray-200 rounded-xl text-sm focus:border-[#4286F4] focus:outline-none transition-colors"
                      data-testid="contact-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#333] mb-1.5">
                      Website
                    </label>
                    <input 
                      type="url"
                      placeholder="https://"
                      value={contactForm.website}
                      onChange={(e) => setContactForm({...contactForm, website: e.target.value})}
                      className="w-full h-12 px-4 border border-gray-200 rounded-xl text-sm focus:border-[#4286F4] focus:outline-none transition-colors"
                      data-testid="contact-website"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#333] mb-1.5">
                    {language === 'nl' ? 'Bericht' : 'Message'}
                  </label>
                  <textarea 
                    rows={5}
                    required
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#4286F4] focus:outline-none transition-colors resize-none"
                    data-testid="contact-message"
                  />
                </div>
                <button 
                  type="submit"
                  disabled={sending}
                  className="bg-[#4286F4] text-white font-semibold px-8 py-3.5 rounded-full hover:bg-[#3070d4] transition-colors inline-flex items-center gap-2 disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {sending 
                    ? (language === 'nl' ? 'Verzenden...' : 'Sending...')
                    : (language === 'nl' ? 'Verstuur' : 'Send')
                  }
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
